
import { useEffect, useState } from "react";
import { useUiStore } from "@/store/ui";
import { useAuthStore } from "@/store/auth";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/brand/logo";
import { BrandWordmark } from "@/components/brand/wordmark";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import {
  ArrowLeft,
  Mail,
  Lock,
  User as UserIcon,
  Phone,
  ShieldCheck,
  Check,
  Truck,
  Store,
  Building2,
  ShoppingBag,
  Eye,
  EyeOff,
  KeyRound,
  ChevronDown,
} from "lucide-react";
import { COUNTRIES, DEFAULT_COUNTRY_CODE } from "@/lib/countries";
import { useTranslation } from "react-i18next";
import type { Role } from "@/lib/types";

// Password policy: 8+ chars, at least one uppercase, at least one digit.
const PASSWORD_RE = /^(?=.*[A-Z])(?=.*\d).{8,}$/;

type Flow = "register" | "login" | "verify" | "role" | "forgot" | "reset";

/**
 * Compute a stable device fingerprint (SHA-256 hex) from browser attributes.
 * Sent to the server on login + verify-login so sessions can be attributed to
 * a device (Phase 5). Returns null until computed (crypto.subtle is async).
 *
 * NOTE: this is a fingerprint for session attribution + new-device detection,
 * NOT a tracker. The hash is scoped per-user on the server (userId+fingerprint
 * is unique) and the raw attributes are never stored.
 */
async function computeDeviceFingerprint(): Promise<string | null> {
  if (typeof window === "undefined") return null;
  try {
    const n = navigator;
    const parts = [
      n.userAgent ?? "",
      n.language ?? "",
      (n as any).platform ?? "",
      `${screen.width}x${screen.height}`,
      `${screen.colorDepth}`,
      Intl.DateTimeFormat().resolvedOptions().timeZone ?? "",
      String(new Date().getTimezoneOffset()),
    ];
    const data = new TextEncoder().encode(parts.join("|"));
    const buf = await crypto.subtle.digest("SHA-256", data);
    return Array.from(new Uint8Array(buf))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
  } catch {
    return null;
  }
}

export function AuthView({ mode }: { mode: string }) {
  const navigate = useUiStore((s) => s.navigate);
  const setUser = useAuthStore((s) => s.setUser);
  const { t } = useTranslation();

  // Internal flow state machine.
  // register -> verify -> role -> (navigate to dashboard)
  // login    -> (navigate to dashboard)   [no OTP for login]
  // forgot   -> reset  -> login
  // The "role" step is reachable via the in-component state OR via the
  // `#/role-select` hash route (used when the user just verified their email
  // and the redirect logic would otherwise send them straight to the app).
  const [flow, setFlow] = useState<Flow>(
    mode === "verify"
      ? "verify"
      : mode === "role-select"
      ? "role"
      : (mode as Flow)
  );

  // Form fields
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [countryCode, setCountryCode] = useState<string>(DEFAULT_COUNTRY_CODE);
  const [code, setCode] = useState("");
  const [devCode, setDevCode] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  // Default to "customer" — the only role that's instant (no application
  // needed). Provider/driver require a verified application first.
  const [selectedRole, setSelectedRole] = useState<Role>("customer");
  // Phase 5: remember-me + device fingerprint.
  const [rememberMe, setRememberMe] = useState(false);
  const [deviceFingerprint, setDeviceFingerprint] = useState<string | null>(null);

  // Phase 5: reset-password form fields.
  const [newPassword, setNewPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);

  // Compute the device fingerprint once on mount.
  useEffect(() => {
    let cancelled = false;
    computeDeviceFingerprint().then((fp) => {
      if (!cancelled && fp) setDeviceFingerprint(fp);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const isRegister = flow === "register";

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name || !email || !password) return toast.error(t("auth.errFillAll"));
    if (!phone) return toast.error(t("auth.errPhoneRequired"));
    if (!PASSWORD_RE.test(password)) {
      return toast.error(t("auth.passwordWeak"));
    }
    if (password !== confirmPassword) {
      return toast.error(t("auth.passwordMismatch"));
    }
    if (!acceptTerms) {
      return toast.error(t("auth.termsRequired"));
    }
    setLoading(true);
    const res = await api.auth.register({
      name,
      email,
      password,
      phone,
      countryCode,
    });
    setLoading(false);
    if (!res.ok) return toast.error(res.error || t("auth.errRegistrationFailed"));
    setDevCode(res.data?.devCode ?? null);
    setFlow("verify");
    toast.success(t("auth.codeSentToEmail"));
  };

  // ---- Fast login: phone + countryCode + password → dashboard (NO OTP) ----
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!phone) return toast.error(t("auth.enterPhone"));
    if (!password) return toast.error(t("auth.enterPassword"));
    setLoading(true);
    const res = await api.auth.login({
      phone,
      countryCode,
      password,
      rememberMe,
      deviceFingerprint: deviceFingerprint ?? undefined,
    });
    setLoading(false);
    if (!res.ok) return toast.error(res.error || t("auth.loginFailed"));
    // Login authenticates directly with phone + password (no OTP). Enter the
    // app right away — the role-specific dashboard is rendered by App.tsx
    // based on user.isAdmin / user.activeRole.
    setUser(res.data!.user);
    toast.success(t("auth.welcomeBack", { name: res.data!.user.name }));
    navigate(res.data!.user.isAdmin ? "admin" : "app");
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (code.length !== 6) return toast.error(t("auth.enterCode"));
    setLoading(true);
    // Verify is only reached via the registration flow (login no longer uses
    // an OTP). Confirm the email and continue to role selection.
    const body = {
      email,
      code,
      name,
      password,
      phone: phone || undefined,
      countryCode,
      // Role is chosen on the next step; pass customer for now, will be
      // updated via PATCH /me after the user picks provider|driver.
      role: "customer",
      deviceFingerprint: deviceFingerprint ?? undefined,
    };
    const res = await api.auth.verifyOtp(body);
    setLoading(false);
    if (!res.ok) return toast.error(res.error || t("auth.invalidCode"));

    // Registration: navigate to the role-selection route BEFORE setting the
    // user, so that when the redirect effect in page.tsx fires (triggered
    // by setUser updating the auth store), the route is already "role-select"
    // and the redirect logic allows it to stay.
    navigate("role-select");
    setUser(res.data!.user);
    setFlow("role");
    toast.success(t("auth.emailVerified"));
  };

  const handleRoleContinue = async () => {
    const user = useAuthStore.getState().user;
    if (!user) {
      // Defensive: shouldn't happen, but recover gracefully.
      navigate("login");
      return;
    }

    // Customer is the instant role — the user was already created with
    // activeRole=customer at registration. Just enter the app.
    if (selectedRole === "customer") {
      toast.success(t("auth.welcome", { name: user.name }));
      navigate(user.isAdmin ? "admin" : "app");
      return;
    }

    // Provider / driver require a verified application. Redirect to the
    // role-application page so the user can submit the required documents.
    // We do NOT call PATCH /me here — the backend rejects activeRole changes
    // to an unverified role (me/route.ts guards). The user stays a customer
    // until an admin approves their application.
    if (selectedRole === "provider" || selectedRole === "driver") {
      toast.info(t("auth.roleRedirectToApplication", { role: selectedRole }));
      navigate("role-application");
      return;
    }

    // Admin cannot be self-assigned — fall back to customer.
    toast.success(t("auth.welcome", { name: user.name }));
    navigate("app");
  };

  // ---- Phase 5: forgot password ----
  const handleForgot = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return toast.error(t("auth.enterEmail"));
    setLoading(true);
    const res = await api.auth.forgotPassword({ email });
    setLoading(false);
    if (!res.ok) return toast.error(res.error || t("auth.sendResetFailed"));
    setDevCode(res.data?.devCode ?? null);
    setCode("");
    setNewPassword("");
    setConfirmPassword("");
    setFlow("reset");
    toast.success(t("auth.resetCodeSent"));
  };

  const handleReset = async (e: React.FormEvent) => {
    e.preventDefault();
    if (code.length !== 6) return toast.error(t("auth.enterCode"));
    if (!PASSWORD_RE.test(newPassword)) {
      return toast.error(t("auth.passwordWeak"));
    }
    if (newPassword !== confirmPassword) {
      return toast.error(t("auth.passwordMismatch"));
    }
    setLoading(true);
    const res = await api.auth.resetPassword({
      email,
      code,
      password: newPassword,
    });
    setLoading(false);
    if (!res.ok) return toast.error(res.error || t("auth.resetFailed"));

    // Password was reset + all sessions revoked. Clear local form state and
    // route the user back to the login screen.
    setCode("");
    setNewPassword("");
    setConfirmPassword("");
    setPassword("");
    setDevCode(null);
    setFlow("login");
    toast.success(t("auth.passwordReset"));
  };

  return (
    <div className="flex min-h-screen flex-col bg-muted/20">
      <header className="border-b border-border/60 bg-background">
        <div className="mx-auto flex h-14 max-w-md items-center gap-2 px-4">
          <Button variant="ghost" size="icon" className="h-9 w-9" onClick={() => navigate("")}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="flex items-center gap-2">
            <Logo className="h-7 w-7" />
            <BrandWordmark />
          </div>
        </div>
      </header>

      <main className="flex flex-1 items-center justify-center px-4 py-8">
        <div className="w-full max-w-md">
          {/* Step indicator (register flow only) */}
          {(flow === "register" || flow === "verify" || flow === "role") && (
            <div className="mb-4 flex items-center justify-center gap-2 text-xs">
              <StepDot active={flow === "register"} done={flow === "verify" || flow === "role"} label={t("auth.stepAccount")} />
              <span className="h-px w-6 bg-border" />
              <StepDot active={flow === "verify"} done={flow === "role"} label={t("auth.stepEmail")} />
              <span className="h-px w-6 bg-border" />
              <StepDot active={flow === "role"} done={false} label={t("auth.stepRole")} />
            </div>
          )}

          {/* ---- Step 1: register or login ---- */}
          {flow === "register" || flow === "login" ? (
            <Card>
              <CardHeader className="text-center">
                <div className="mx-auto mb-3 flex h-16 w-16 items-center justify-center">
                  <Logo className="h-16 w-16" />
                </div>
                <CardTitle className="text-2xl">{isRegister ? t("auth.registerTitle") : t("auth.signInTitle")}</CardTitle>
                <CardDescription>
                  {isRegister
                    ? t("auth.registerSubtitle")
                    : t("auth.signInSubtitle")}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={isRegister ? handleRegister : handleLogin} className="space-y-3">
                  {isRegister && (
                    <div className="space-y-1.5">
                      <Label htmlFor="name">{t("auth.nameLabel")}</Label>
                      <div className="relative">
                        <UserIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <Input id="name" className="pl-9" placeholder={t("auth.namePlaceholder")} value={name} onChange={(e) => setName(e.target.value)} />
                      </div>
                    </div>
                  )}
                  {isRegister && (
                    <div className="space-y-1.5">
                      <Label htmlFor="email">{t("auth.emailLabel")}</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <Input id="email" type="email" className="pl-9" placeholder={t("auth.emailPlaceholder")} value={email} onChange={(e) => setEmail(e.target.value)} />
                      </div>
                    </div>
                  )}

                  {/* ---- Phone + country code (login primary identifier; required for registration too) ---- */}
                  <div className="space-y-1.5">
                    <Label htmlFor="phone">
                      {t("auth.phoneLabel")}
                      {isRegister && <span className="ml-1 text-xs text-destructive">*</span>}
                    </Label>
                    <div className="flex gap-2">
                      <div className="relative w-36 shrink-0">
                        <Phone className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <select
                          aria-label={t("auth.countryCodeLabel")}
                          className="h-9 w-full appearance-none rounded-md border border-input bg-background pl-9 pr-7 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                          value={countryCode}
                          onChange={(e) => setCountryCode(e.target.value)}
                        >
                          {COUNTRIES.map((c) => (
                            <option key={c.code} value={c.code}>
                              {c.flag} {c.code}
                            </option>
                          ))}
                        </select>
                        <ChevronDown className="pointer-events-none absolute right-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                      </div>
                      <div className="relative flex-1">
                        <Input
                          id="phone"
                          className="pl-3"
                          placeholder={t("auth.phonePlaceholder")}
                          value={phone}
                          onChange={(e) => setPhone(e.target.value)}
                          inputMode="tel"
                          autoComplete="tel-national"
                        />
                      </div>
                    </div>
                    {isRegister && (
                      <p className="text-[11px] text-muted-foreground">
                        {t("auth.phoneSignInHint")}
                      </p>
                    )}
                  </div>

                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="password">{t("auth.passwordLabel")}</Label>
                      {!isRegister && (
                        <button
                          type="button"
                          className="text-xs font-medium text-primary hover:underline"
                          onClick={() => {
                            setDevCode(null);
                            setCode("");
                            setFlow("forgot");
                          }}
                        >
                          {t("auth.forgotPassword")}
                        </button>
                      )}
                    </div>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                      <Input
                        id="password"
                        type={showPassword ? "text" : "password"}
                        className="pl-9 pr-9"
                        placeholder={t("auth.passwordPlaceholder")}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        autoComplete={isRegister ? "new-password" : "current-password"}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((v) => !v)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        aria-label={showPassword ? t("auth.hidePassword") : t("auth.showPassword")}
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                    {isRegister && (
                      <p className="text-[11px] text-muted-foreground">
                        {t("auth.passwordHint")}
                      </p>
                    )}
                  </div>

                  {isRegister && (
                    <>
                      <div className="space-y-1.5">
                        <Label htmlFor="confirm">{t("auth.confirmPasswordLabel")}</Label>
                        <div className="relative">
                          <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                          <Input
                            id="confirm"
                            type={showConfirm ? "text" : "password"}
                            className="pl-9 pr-9"
                            placeholder={t("auth.confirmPasswordPlaceholder")}
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                          />
                          <button
                            type="button"
                            onClick={() => setShowConfirm((v) => !v)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                            aria-label={showConfirm ? t("auth.hidePassword") : t("auth.showPassword")}
                          >
                            {showConfirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </button>
                        </div>
                        {confirmPassword.length > 0 && password === confirmPassword && (
                          <p className="flex items-center gap-1 text-[11px] text-emerald-600">
                            <Check className="h-3 w-3" /> {t("auth.passwordsMatch")}
                          </p>
                        )}
                      </div>

                      {/* Terms & Trust Policy acceptance */}
                      <div className="flex items-start gap-2 rounded-lg border border-border/60 bg-muted/30 p-3">
                        <Checkbox
                          id="terms"
                          checked={acceptTerms}
                          onCheckedChange={(v) => setAcceptTerms(v === true)}
                          className="mt-0.5"
                        />
                        <label htmlFor="terms" className="cursor-pointer text-xs leading-relaxed text-muted-foreground">
                          {t("auth.termsAccept")}{" "}
                          <button
                            type="button"
                            className="font-semibold text-primary hover:underline"
                            onClick={() => navigate("legal/terms")}
                          >
                            {t("auth.termsTrustPolicy")}
                          </button>
                          .
                        </label>
                      </div>
                    </>
                  )}

                  {!isRegister && (
                    /* Phase 5: remember-me checkbox on login. */
                    <div className="flex items-center gap-2 rounded-lg border border-border/60 bg-muted/30 px-3 py-2">
                      <Checkbox
                        id="rememberMe"
                        checked={rememberMe}
                        onCheckedChange={(v) => setRememberMe(v === true)}
                      />
                      <label htmlFor="rememberMe" className="cursor-pointer text-sm text-muted-foreground">
                        {t("auth.rememberMe")}
                      </label>
                    </div>
                  )}

                  <Button type="submit" className="w-full" disabled={loading}>
                    {loading ? t("auth.pleaseWait") : isRegister ? t("auth.registerButton") : t("auth.signInButton")}
                  </Button>
                </form>

                {isRegister ? (
                  <div className="mt-4 text-center text-sm text-muted-foreground">
                    {t("auth.haveAccount")}{" "}
                    <button
                      className="font-semibold text-primary hover:underline"
                      onClick={() => setFlow("login")}
                    >
                      {t("auth.signInButton")}
                    </button>
                  </div>
                ) : (
                  <div className="mt-4 text-center text-sm text-muted-foreground">
                    <button
                      className="font-semibold text-primary hover:underline"
                      onClick={() => setFlow("register")}
                    >
                      {t("auth.noAccount")}
                    </button>
                  </div>
                )}
              </CardContent>
            </Card>
          ) : flow === "verify" ? (
            /* ---- Step 2: email OTP ---- */
            <Card>
              <CardHeader className="text-center">
                <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10">
                  <ShieldCheck className="h-6 w-6 text-primary" />
                </div>
                <CardTitle className="text-2xl">{t("auth.verifyTitle")}</CardTitle>
                <CardDescription>
                  {t("auth.otpSent")}{" "}
                  <span className="font-semibold text-foreground">{email}</span>
                </CardDescription>
              </CardHeader>
              <CardContent>
                {devCode && (
                  <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-center text-sm text-amber-800 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-200">
                    <span className="font-semibold">{t("common.demoMode")}:</span> {t("auth.demoCode")}{" "}
                    <span className="font-mono font-bold tracking-widest">{devCode}</span>
                  </div>
                )}
                <form onSubmit={handleVerify} className="space-y-4">
                  <div className="flex justify-center">
                    <InputOTP maxLength={6} value={code} onChange={(v) => setCode(v)}>
                      <InputOTPGroup>
                        <InputOTPSlot index={0} />
                        <InputOTPSlot index={1} />
                        <InputOTPSlot index={2} />
                        <InputOTPSlot index={3} />
                        <InputOTPSlot index={4} />
                        <InputOTPSlot index={5} />
                      </InputOTPGroup>
                    </InputOTP>
                  </div>
                  <Button type="submit" className="w-full" disabled={loading || code.length !== 6}>
                    {loading ? t("auth.verifying") : t("auth.verifyButton")}
                  </Button>
                  <div className="text-center">
                    <button
                      type="button"
                      className="text-sm text-muted-foreground hover:underline"
                      onClick={() => {
                        setFlow("register");
                        setCode("");
                      }}
                    >
                      ← {t("common.back")}
                    </button>
                  </div>
                </form>
              </CardContent>
            </Card>
          ) : flow === "forgot" ? (
            /* ---- Phase 5: forgot password — enter email ---- */
            <Card>
              <CardHeader className="text-center">
                <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10">
                  <KeyRound className="h-6 w-6 text-primary" />
                </div>
                <CardTitle className="text-2xl">{t("auth.resetPasswordTitle")}</CardTitle>
                <CardDescription>
                  {t("auth.resetPasswordDesc")}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleForgot} className="space-y-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="forgot-email">{t("auth.emailLabel")}</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                      <Input
                        id="forgot-email"
                        type="email"
                        className="pl-9"
                        placeholder={t("auth.emailPlaceholder")}
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                      />
                    </div>
                  </div>
                  <Button type="submit" className="w-full" disabled={loading || !email}>
                    {loading ? t("profile.sending") : t("auth.sendResetCode")}
                  </Button>
                  <div className="text-center">
                    <button
                      type="button"
                      className="text-sm text-muted-foreground hover:underline"
                      onClick={() => {
                        setFlow("login");
                        setDevCode(null);
                      }}
                    >
                      ← {t("common.back")}
                    </button>
                  </div>
                </form>
              </CardContent>
            </Card>
          ) : flow === "reset" ? (
            /* ---- Phase 5: reset password — enter code + new password ---- */
            <Card>
              <CardHeader className="text-center">
                <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10">
                  <ShieldCheck className="h-6 w-6 text-primary" />
                </div>
                <CardTitle className="text-2xl">{t("auth.setNewPasswordTitle")}</CardTitle>
                <CardDescription>
                  {t("auth.setNewPasswordDescPre")}{" "}
                  <span className="font-semibold text-foreground">{email}</span>{" "}
                  {t("auth.setNewPasswordDescPost")}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {devCode && (
                  <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-center text-sm text-amber-800 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-200">
                    <span className="font-semibold">{t("common.demoMode")}:</span> {t("auth.demoCode")}{" "}
                    <span className="font-mono font-bold tracking-widest">{devCode}</span>
                  </div>
                )}
                <form onSubmit={handleReset} className="space-y-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="reset-code">{t("auth.verificationCode")}</Label>
                    <div className="flex justify-center">
                      <InputOTP maxLength={6} value={code} onChange={(v) => setCode(v)}>
                        <InputOTPGroup>
                          <InputOTPSlot index={0} />
                          <InputOTPSlot index={1} />
                          <InputOTPSlot index={2} />
                          <InputOTPSlot index={3} />
                          <InputOTPSlot index={4} />
                          <InputOTPSlot index={5} />
                        </InputOTPGroup>
                      </InputOTP>
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="new-password">{t("auth.newPasswordLabel")}</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                      <Input
                        id="new-password"
                        type={showNewPassword ? "text" : "password"}
                        className="pl-9 pr-9"
                        placeholder={t("auth.newPasswordPlaceholder")}
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewPassword((v) => !v)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        aria-label={showNewPassword ? t("auth.hidePassword") : t("auth.showPassword")}
                      >
                        {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                    <p className="text-[11px] text-muted-foreground">
                      {t("auth.passwordHint")}
                    </p>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="reset-confirm">{t("auth.confirmNewPasswordLabel")}</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                      <Input
                        id="reset-confirm"
                        type={showConfirm ? "text" : "password"}
                        className="pl-9 pr-9"
                        placeholder={t("auth.confirmNewPasswordPlaceholder")}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirm((v) => !v)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        aria-label={showConfirm ? t("auth.hidePassword") : t("auth.showPassword")}
                      >
                        {showConfirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                    {confirmPassword.length > 0 && newPassword === confirmPassword && (
                      <p className="flex items-center gap-1 text-[11px] text-emerald-600">
                        <Check className="h-3 w-3" /> {t("auth.passwordsMatch")}
                      </p>
                    )}
                  </div>
                  <Button
                    type="submit"
                    className="w-full"
                    disabled={loading || code.length !== 6 || !newPassword}
                  >
                    {loading ? t("auth.resetting") : t("auth.resetPasswordButton")}
                  </Button>
                  <div className="text-center">
                    <button
                      type="button"
                      className="text-sm text-muted-foreground hover:underline"
                      onClick={() => {
                        setFlow("forgot");
                        setCode("");
                        setDevCode(null);
                      }}
                    >
                      ← {t("common.back")}
                    </button>
                  </div>
                </form>
              </CardContent>
            </Card>
          ) : (
            /* ---- Step 3: role selection (register flow only) ---- */
            <Card>
              <CardHeader className="text-center">
                <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10">
                  <Check className="h-6 w-6 text-primary" />
                </div>
                <CardTitle className="text-2xl">{t("auth.roleTitle")}</CardTitle>
                <CardDescription>{t("auth.roleSubtitle")}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <RoleOption
                  icon={ShoppingBag}
                  title={t("auth.roleCustomer")}
                  desc={t("auth.roleCustomerHint")}
                  selected={selectedRole === "customer"}
                  onClick={() => setSelectedRole("customer")}
                />
                <RoleOption
                  icon={Store}
                  title={t("auth.roleProvider")}
                  desc={t("auth.roleProviderHint")}
                  selected={selectedRole === "provider"}
                  onClick={() => setSelectedRole("provider")}
                />
                <RoleOption
                  icon={Truck}
                  title={t("auth.roleDriver")}
                  desc={t("auth.roleDriverHint")}
                  selected={selectedRole === "driver"}
                  onClick={() => setSelectedRole("driver")}
                />
                {/* Admin option shown for transparency but disabled for self-registration */}
                <RoleOption
                  icon={Building2}
                  title={t("auth.roleAdmin")}
                  desc={t("auth.roleAdminHint")}
                  selected={selectedRole === "admin"}
                  onClick={() => toast.info(t("auth.adminGrantedInfo"))}
                  disabled
                />
                <Button className="w-full" onClick={handleRoleContinue} disabled={loading}>
                  {loading ? t("auth.pleaseWait") : t("auth.roleContinue")}
                </Button>
              </CardContent>
            </Card>
          )}

          <p className="mt-6 text-center text-xs text-muted-foreground">
            {t("auth.continueAgree")}{" "}
            <button onClick={() => navigate("legal/terms")} className="font-semibold text-primary hover:underline">
              {t("auth.termsTrustPolicy")}
            </button>
            .
          </p>
          <div className="mt-3 flex items-center justify-center gap-1.5 text-xs text-muted-foreground">
            <Logo className="h-5 w-5" />
            <span className="font-semibold text-foreground">{t("common.appName")}</span>
            <span>{t("landing.footerLegal.copyright")}</span>
          </div>
        </div>
      </main>
    </div>
  );
}

function StepDot({ active, done, label }: { active: boolean; done: boolean; label: string }) {
  return (
    <span
      className={`flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${
        done
          ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300"
          : active
          ? "bg-primary text-primary-foreground"
          : "bg-muted text-muted-foreground"
      }`}
    >
      {label}
    </span>
  );
}

function RoleOption({
  icon: Icon,
  title,
  desc,
  selected,
  onClick,
  disabled,
}: {
  icon: React.ElementType;
  title: string;
  desc: string;
  selected: boolean;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`flex w-full items-center gap-3 rounded-xl border p-4 text-left transition ${
        selected ? "border-primary bg-primary/5" : "border-border hover:border-primary/40"
      } ${disabled ? "cursor-not-allowed opacity-60" : ""}`}
    >
      <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${selected ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div className="flex-1">
        <p className="font-semibold">{title}</p>
        <p className="text-xs text-muted-foreground">{desc}</p>
      </div>
      {selected && <Check className="h-4 w-4 text-primary" />}
    </button>
  );
}
